import {
  enqueueAtomicRpcV1,
  type AtomicRpcHandle,
} from '../sync/atomicRpcOutbox.js';

export const SUPPLIER_ITEM_MATCH_MANAGEMENT_SCHEMA =
  'trackeat.supplier-item-match-management' as const;
export const SUPPLIER_ITEM_MATCH_MANAGEMENT_RESULT_SCHEMA =
  'trackeat.supplier-item-match-management-result' as const;

export interface SupplierItemMatchManagementRequestV1 extends Record<string, unknown> {
  schema: typeof SUPPLIER_ITEM_MATCH_MANAGEMENT_SCHEMA;
  version: 1;
  request_id: string;
  tenant_id: string;
  business_id: string;
  supplier_id: string;
  food_id: string;
  supplier_code: string | null;
  raw_description_norm: string | null;
  action: 'move' | 'delete';
  target_food_id: string | null;
  requested_at: string;
}

export interface QueueSupplierItemMatchManagementInputV1 {
  requestId: string;
  tenantId: string;
  businessId: string;
  supplierId: string;
  foodId: string;
  supplierCode: string | null;
  rawDescriptionNorm: string | null;
  action: 'move' | 'delete';
  targetFoodId: string | null;
  requestedAt: string;
}

function requireUuid(value: string, field: string): void {
  if (!/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value)) {
    throw new Error(`supplier_item_match_management_invalid_${field}`);
  }
}

export async function queueSupplierItemMatchManagementV1(
  input: QueueSupplierItemMatchManagementInputV1,
): Promise<AtomicRpcHandle> {
  requireUuid(input.requestId, 'request_id');
  requireUuid(input.tenantId, 'tenant_id');
  requireUuid(input.businessId, 'business_id');
  requireUuid(input.supplierId, 'supplier_id');
  requireUuid(input.foodId, 'food_id');
  const supplierCode = input.supplierCode?.trim() || null;
  const rawDescriptionNorm = input.rawDescriptionNorm?.trim() || null;
  if ((supplierCode === null) === (rawDescriptionNorm === null)) {
    throw new Error('supplier_item_match_management_invalid_selector');
  }
  if (input.action === 'move') {
    if (!input.targetFoodId) {
      throw new Error('supplier_item_match_management_target_required');
    }
    requireUuid(input.targetFoodId, 'target_food_id');
    if (input.targetFoodId === input.foodId) {
      throw new Error('supplier_item_match_management_target_matches_source');
    }
  } else if (input.action !== 'delete' || input.targetFoodId !== null) {
    throw new Error('supplier_item_match_management_invalid_action');
  }
  if (!Number.isFinite(Date.parse(input.requestedAt))) {
    throw new Error('supplier_item_match_management_invalid_requested_at');
  }

  const request: SupplierItemMatchManagementRequestV1 = {
    schema: SUPPLIER_ITEM_MATCH_MANAGEMENT_SCHEMA,
    version: 1,
    request_id: input.requestId,
    tenant_id: input.tenantId,
    business_id: input.businessId,
    supplier_id: input.supplierId,
    food_id: input.foodId,
    supplier_code: supplierCode,
    raw_description_norm: rawDescriptionNorm,
    action: input.action,
    target_food_id: input.targetFoodId,
    requested_at: input.requestedAt,
  };
  const queued = await enqueueAtomicRpcV1({
    kind: 'supplier_item_match_management_v1',
    tenantId: input.tenantId,
    businessId: input.businessId,
    scopeKey: `supplier-item-match:${input.supplierId}:${supplierCode ?? rawDescriptionNorm}`,
    request,
    optimisticMasters: [],
    now: input.requestedAt,
  });
  return queued.handle;
}

export function parseSupplierItemMatchManagementResultV1(
  value: unknown,
  request: SupplierItemMatchManagementRequestV1,
  requestDigest: string,
): void {
  if (!value || typeof value !== 'object') {
    throw new Error('supplier_item_match_management_invalid_receipt');
  }
  const result = value as Record<string, unknown>;
  if (
    result.schema !== SUPPLIER_ITEM_MATCH_MANAGEMENT_RESULT_SCHEMA
    || result.version !== 1
    || result.request_id !== request.request_id
    || result.request_digest !== requestDigest
    || result.tenant_id !== request.tenant_id
    || result.supplier_id !== request.supplier_id
    || result.food_id !== request.food_id
    || result.action !== request.action
    || result.target_food_id !== request.target_food_id
    || typeof result.affected_count !== 'number'
    || typeof result.replayed !== 'boolean'
  ) {
    throw new Error('supplier_item_match_management_receipt_mismatch');
  }
}
